
import { useNavigate, } from 'react-router-dom';
import axios, { HttpStatusCode } from 'axios';
import { Formik, Form, FormikErrors } from 'formik';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from "../../i18n/useTranslation";
import { toast } from 'react-toastify';
import styles from './ContactUsForm.module.scss';
import * as yup from 'yup';
import { Country } from '../order/models';
import { ContactUsFormViewModel } from './contact-us-form-view.model';
import { IContactUsDto } from './contact-us-form-dto.interface';
import { Validator } from '../../shared/constants/validator';
import { contactUs, getLinkToContactSuccess, htmlId } from './fixcenter-support.service';
import classNames from 'classnames';
import globalStyles from "../../global-styles.module.scss"

export interface IContactUsFormInput {
    isFromContactUs: boolean;
}

const ContactUsForm: React.FC<IContactUsFormInput> = ({isFromContactUs}) => {
    const { t } = useTranslation('translation');
    const [countries, setCountries] = useState<Country[]>([]);

    const navigate = useNavigate();
    const showError = (message: string) => toast.error(message);

    useEffect(() => {
        axios.get('countries')
            .then(response => {
                const countries = response?.data as Country[];
                setCountries(countries || []);
            })
    }, []);

    const getModelDto = (values: ContactUsFormViewModel): IContactUsDto => {
        const dto: IContactUsDto = {
            name: values.name,
            company: values.company,
            email: values.email,
            phone: values.phone,
            postalCode: values.postalCode,
            country: values.country,
            message: values.message,
            jsonMessages: JSON.stringify(''),
            title: 'Fix center (' +  (isFromContactUs ? t('CONTACT_US').toUpperCase() : t('header.VISIT US').toUpperCase()) + ')',
            files: []
        }
        return dto;
    }

    const isValid = (errors: FormikErrors<ContactUsFormViewModel>): boolean => {
        return Object.keys(errors).length === 0;
    }
    
    const validationSchema = yup.object<ContactUsFormViewModel>().shape({
        name: yup.string().required().min(Validator.minLengthName).max(Validator.maxLengthName),
        company: yup.string().min(Validator.minLengthName).max(Validator.maxLengthName),
        email: yup.string().required().email(),
        phone: yup.string().matches(Validator.phoneNumberRegExp),
        postalCode: yup.string().required().max(Validator.maxLengthName),
        country: yup.string().required(),
        message: yup.string().required().max(Validator.textAreaLengthLimit),
    });

    const displayFillRequiredData = (errors: FormikErrors<ContactUsFormViewModel>) => {
        if (!isValid(errors)) {
            showError(t('register.fillAllRequiredData'));
        }
    }

    const send = (values: ContactUsFormViewModel, errors: FormikErrors<ContactUsFormViewModel>): void | Promise<any> => {
        if (isValid(errors)) {
            contactUs(getModelDto(values)).then((response) => {
                if (response && response.status === HttpStatusCode.Ok) {
                    navigate(getLinkToContactSuccess());
                } else {
                    showError(t('commonErrors.SENDING_MESSAGE'));
                }
            }).catch(() => showError(t('commonErrors.SENDING_MESSAGE')));
        }
    }

    return (
        <div className={styles.form_container}>
            <Formik 
                validateOnMount 
                initialValues={new ContactUsFormViewModel()}
                validationSchema={validationSchema}
                onSubmit={() => {}}
            >{({
                values, handleChange, errors, touched }) =>
                <Form
                    autoComplete='off'
                    spellCheck='false'
                    className={styles.form} 
                    onMouseEnter={() => { }} 
                    onSubmit={(e) => {
                        e.preventDefault();
                        send(values, errors);
                    }}>
                    <div className={styles.form_content}>   
                        <div className={styles.content}>
                            <label className={styles.main_header_text}>
                                {
                                isFromContactUs ? t("CONTACT_US") : t("header.VISIT US")
                                }
                            </label>
                            {!isFromContactUs &&
                            <div className={styles.form_row}>
                                <p>{t("VISIT_US_DEF.GET_IN_TOUCH_WITH_FIXCENTER")}</p>
                            </div>
                            }
                            <div className={styles.form_row}>
                                <label className={styles.header_text}>{t('NAME')}*</label>
                                <input
                                    ref={(e) => {
                                        if (e) {
                                            errors['name'] ? e.setCustomValidity(t(errors['name'])) :  e.setCustomValidity('');          
                                        }
                                    }}
                                    id = {`${htmlId}-name-input`}
                                    name="name" 
                                    value={values.name}
                                    onChange={handleChange}
                                >
                                </input>
                            </div>
                            <div className={styles.form_row}>
                                <label className={styles.header_text}>{t('COMPANY')}</label>
                                <input
                                ref={(e) => {
                                    if (e) {
                                            errors['company'] ? e.setCustomValidity(t(errors['company'])) :  e.setCustomValidity('');          
                                        }
                                    }}
                                    id = {`${htmlId}-company-name-input`}
                                    name="company" 
                                    value={values.company} 
                                    onChange={handleChange}
                                >
                                </input>
                            </div>

                            <div className={styles.form_row}>
                                <label className={styles.header_text}>{t('EMAIL')}*</label>
                                <input
                                    ref={(e) => {
                                        if (e) {
                                            errors['email'] ? e.setCustomValidity(t(errors['email'])) :  e.setCustomValidity('');          
                                        }
                                    }}
                                    id = {`${htmlId}-email-input`}
                                    name="email"
                                    type='email'
                                    value={values.email}
                                    onChange={handleChange}
                                >
                                </input>
                            </div>

                            <div className={styles.form_row}>
                                <label className={styles.header_text}>{t('PHONE')}</label>
                                <input
                                    ref={(e) => {
                                        if (e) {
                                            errors['phone'] ? e.setCustomValidity(t(Validator.translateMainCode + 'PHONE_NUMBER')) :  e.setCustomValidity('');          
                                        }
                                    }}
                                    id = {`${htmlId}-phone-input`}
                                    name="phone" 
                                    value={values.phone} 
                                    onChange={handleChange}
                                >
                                </input>
                            </div>

                            <div className={styles.form_row}>
                                <label className={styles.header_text}>{t('ZIP')}*</label>
                                <input
                                    ref={(e) => {
                                        if (e) {
                                            errors['postalCode'] ? e.setCustomValidity(t(errors['postalCode'])) :  e.setCustomValidity('');          
                                        }
                                    }}
                                    id = {`${htmlId}-zip-input`}
                                    name="postalCode"
                                    value={values.postalCode}
                                    onChange={handleChange}
                                >
                                </input>
                            </div>

                            <div className={styles.form_row}>
                                <label className={styles.header_text}>{t('COUNTRY')}*</label>
                                <select
                                    ref={(e) => {
                                        if (e) {
                                            errors['country'] ? e.setCustomValidity(t(errors['country'])) :  e.setCustomValidity('');          
                                        }
                                    }}
                                    id = {`${htmlId}-country-code-select`}
                                    name="country" 
                                    value={values.country} onChange={handleChange}>
                                    <option disabled></option>
                                    {countries.map((country: any, index: number) => (
                                        <option 
                                            key={index} 
                                            value={country?.name}>
                                            {country?.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className={styles.form_row}>
                                <label className={styles.header_text}>{t('MESSAGE')}*</label>
                                <textarea
                                    ref={(e) => {
                                        if (e) {
                                            errors['message'] ? e.setCustomValidity(t(errors['message'])) :  e.setCustomValidity('');          
                                        }
                                    }}
                                    id = {`${htmlId}-message-textarea`}
                                    name="message"
                                    value={values.message}
                                    onChange={handleChange}
                                >
                                </textarea>
                            </div>
                            
                            <div className={styles.button_content}>
                                <button
                                    className={styles.send_btn}
                                    type='submit'
                                    id = {`${htmlId}-submit-btn`}
                                    onClick={() => {
                                        displayFillRequiredData(errors)
                                    }}
                                >
                                <span>{t("SEND")}</span>  
                                </button>
                            </div>
                        </div>
                    </div>
                </Form >
                }
            </Formik >
            {!isFromContactUs &&
                <div className={globalStyles.flex_center_item}>
                    <div className={classNames(globalStyles.fixcenter_support_visit_us_sprites, globalStyles.img_3)}></div>
                </div>
            }
        </div>
    );
};

export default ContactUsForm;